import React, { useEffect, useState } from 'react';
import { FormFieldDef, FormGroupDef } from '../../../interfaces/form-config.interface';
import { formConfigService } from '../../../services/form-config.service';
import Button from '../../../components/ui/Button';
import { getErrorMsg } from '../../../utils/error';

interface GroupsTabProps {
  configId: string;
  fields: FormFieldDef[];
  fieldGroups: FormGroupDef[];
  onGroupsChange: (groups: FormGroupDef[]) => void;
  onFlash: (msg: string) => void;
}

interface GroupRowState {
  allowMultiple: boolean;
  maxInstances: string; // string so input is controlled
  saving: boolean;
  dirty: boolean;
}

export const GroupsTab = ({ configId, fields, fieldGroups, onGroupsChange, onFlash }: GroupsTabProps) => {
  // Unique group names used by at least one field
  const groupNames = Array.from(new Set(fields.filter((f) => f.group).map((f) => f.group!)));

  // Build initial row state from fieldGroups
  const makeState = (name: string): GroupRowState => {
    const def = fieldGroups.find((g) => g.name === name);
    return {
      allowMultiple: def?.allowMultiple ?? false,
      maxInstances: def?.maxInstances != null ? String(def.maxInstances) : '',
      saving: false,
      dirty: false,
    };
  };

  const [rows, setRows] = useState<Record<string, GroupRowState>>(() =>
    Object.fromEntries(groupNames.map((n) => [n, makeState(n)])),
  );

  // Re-sync when fieldGroups or fields change (e.g. after a save)
  useEffect(() => {
    setRows(Object.fromEntries(groupNames.map((n) => [n, makeState(n)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, fieldGroups]);

  const patch = (name: string, update: Partial<GroupRowState>) =>
    setRows((prev) => ({ ...prev, [name]: { ...prev[name], ...update } }));

  const handleSave = async (name: string) => {
    const row = rows[name];
    if (!row) return;
    patch(name, { saving: true });
    try {
      const maxInstances =
        row.allowMultiple && row.maxInstances.trim() !== ''
          ? parseInt(row.maxInstances, 10)
          : null;
      const saved = await formConfigService.upsertGroup(configId, name, row.allowMultiple, maxInstances);
      // Merge into fieldGroups
      const existing = fieldGroups.filter((g) => g.name !== name);
      onGroupsChange([...existing, saved]);
      patch(name, { dirty: false, saving: false });
      onFlash(`Group "${name}" saved`);
    } catch (e: unknown) {
      patch(name, { saving: false });
      onFlash(getErrorMsg(e, `Failed to save group "${name}"`));
    }
  };

  if (groupNames.length === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
        No groups defined. Assign a group name to one or more fields to manage group settings here.
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-2xl">
      <p className="text-sm text-gray-500 dark:text-gray-400">
        When <strong>Allow Multiple</strong> is on, the mobile app shows an{' '}
        <span className="font-mono text-xs bg-gray-100 dark:bg-gray-800 px-1 rounded">+ Add [group name]</span>{' '}
        button letting users duplicate all fields in that group (e.g. add a second engine).
      </p>

      {groupNames.map((name) => {
        const row = rows[name];
        const fieldCount = fields.filter((f) => f.group === name).length;
        if (!row) return null;
        return (
          <div
            key={name}
            className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3"
          >
            <div className="flex items-center justify-between">
              <div>
                <span className="font-medium text-gray-800 dark:text-gray-100">{name}</span>
                <span className="ml-2 text-xs text-gray-400">{fieldCount} field{fieldCount !== 1 ? 's' : ''}</span>
              </div>
              {row.dirty && (
                <Button size="sm" variant="primary" onClick={() => handleSave(name)} disabled={row.saving}>
                  {row.saving ? 'Saving…' : 'Save'}
                </Button>
              )}
            </div>

            <label className="flex items-center gap-3 cursor-pointer select-none">
              <div
                role="switch"
                aria-checked={row.allowMultiple}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  row.allowMultiple ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-600'
                }`}
                onClick={() => patch(name, { allowMultiple: !row.allowMultiple, dirty: true })}
              >
                <span
                  className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${
                    row.allowMultiple ? 'translate-x-4' : 'translate-x-0.5'
                  }`}
                />
              </div>
              <span className="text-sm text-gray-700 dark:text-gray-300">Allow Multiple</span>
            </label>

            {row.allowMultiple && (
              <div className="flex items-center gap-3">
                <label className="text-sm text-gray-600 dark:text-gray-400 w-32 shrink-0">Max instances</label>
                <input
                  type="number"
                  min={1}
                  placeholder="Unlimited"
                  className="w-32 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  value={row.maxInstances}
                  onChange={(e) => patch(name, { maxInstances: e.target.value, dirty: true })}
                />
                <span className="text-xs text-gray-400">Leave blank for unlimited</span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
